import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Mail, ArrowRight, KeyRound, ChevronLeft, Sun, Moon, Building2, ClipboardList, CheckCircle2, Megaphone, QrCode, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth, homeFor } from "@/lib/auth";

const STEPS = [
  [ClipboardList, "Request a hall", "Pick a date and slot — overlaps are blocked."],
  [CheckCircle2,  "Incharge approves", "The auditorium's incharge confirms it."],
  [Megaphone,     "Publish & register", "Open registration and share the page."],
  [QrCode,        "Scan at the gate", "Check attendees in, then issue certificates."],
];
const HALLS = ["BSN Auditorium", "APJ Hall", "A-226", "A-116", "Amphitheatre", "EST", "LBA"];

export default function Login() {
  const { requestOtp, verifyOtp, toggleTheme, theme, isAuthed, user } = useAuth();
  const nav  = useNavigate();
  const loc  = useLocation();
  const from = loc.state?.from || null;

  useEffect(() => {
    if (isAuthed && user) nav(from || homeFor(user), { replace: true });
  }, [isAuthed, user]); // eslint-disable-line

  const [email, setEmail] = useState("");
  const [code,  setCode]  = useState("");
  const [step,  setStep]  = useState("email");
  const [busy,  setBusy]  = useState(false);

  const sendOtp = async (e) => {
    e?.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      await requestOtp(email.trim().toLowerCase());
      toast.success("Code sent — check your inbox");
      setStep("otp");
    } catch (err) { toast.error(err.message || "Could not send code"); }
    finally { setBusy(false); }
  };

  const verifyCode = async (e) => {
    e?.preventDefault();
    if (code.length < 6) { toast.error("Enter the 6-digit code"); return; }
    setBusy(true);
    try {
      const u = await verifyOtp(email.trim().toLowerCase(), code);
      toast.success("Welcome back!");
      nav(from || homeFor(u), { replace: true });
    } catch (err) { toast.error(err.message || "Invalid code"); }
    finally { setBusy(false); }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-background">
      {/* Ambient background — matches the marketing site */}
      <div className="orb bg-cobalt-500/50" style={{ width: 520, height: 520, top: -180, left: -160 }} />
      <div className="orb bg-cyanx/40" style={{ width: 420, height: 420, top: 40, right: -120 }} />
      <div className="orb bg-chartreuse/40" style={{ width: 340, height: 340, bottom: -160, left: "40%" }} />
      <div className="absolute inset-0 grid-lines pointer-events-none" />

      {/* Header */}
      <header className="relative z-10 flex items-center justify-between px-6 lg:px-12 py-5">
        <Link to="/" className="flex items-center gap-2.5">
          <div className="h-8 w-8 rounded-xl bg-primary grid place-items-center shrink-0 shadow-sm shadow-primary/30">
            <Building2 className="h-4 w-4 text-white" />
          </div>
          <div className="font-display text-lg font-extrabold tracking-tighter leading-none">
            BMSIT<span className="text-cobalt-500">&M</span>
            <span className="block text-[10px] font-mono font-normal tracking-widest uppercase text-muted-foreground">Events</span>
          </div>
        </Link>
        <div className="flex items-center gap-2">
          <Link to="/" className="hidden sm:inline text-sm font-medium hover:text-cobalt-500 transition-colors">&larr; Back to site</Link>
          <button onClick={toggleTheme}
            className="w-9 h-9 rounded-xl glass flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors">
            {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>
        </div>
      </header>

      {/* Body */}
      <div className="relative z-10 mx-auto max-w-6xl px-6 lg:px-12 pt-6 pb-16 grid lg:grid-cols-[1.05fr_0.95fr] gap-12 items-center min-h-[calc(100vh-160px)]">
        {/* Left — brand / flow */}
        <div className="hidden lg:block">
          <span className="glass inline-flex items-center gap-1.5 rounded-full px-3 py-1">
            <span className="text-mono text-[10px] tracking-widest uppercase">Events platform · BMSIT&M</span>
          </span>
          <h1 className="mt-5 font-display font-black leading-[0.9] tracking-tighter text-[52px] xl:text-[62px]">
            Every hall<br />
            <span className="italic font-light">on campus,</span><br />
            <span className="bg-clip-text text-transparent bg-[linear-gradient(120deg,#0047FF_0%,#22D3EE_60%,#BEF264_100%)]">one sign-in.</span>
          </h1>
          <p className="mt-5 max-w-md text-muted-foreground leading-relaxed">
            Faculty and clubs request auditoriums, incharges approve them, and organisers run the
            whole event — registration, QR check-in and certificates — from one place.
          </p>

          <div className="mt-8 grid grid-cols-2 gap-3 max-w-lg">
            {STEPS.map(([Icon, title, sub], i) => (
              <div key={title} className="glass rounded-2xl p-3.5">
                <div className="flex items-center gap-2">
                  <span className="text-mono text-[10px] text-cobalt-500">0{i + 1}</span>
                  <Icon className="h-3.5 w-3.5 text-cobalt-500" />
                  <span className="text-sm font-semibold">{title}</span>
                </div>
                <div className="text-xs text-muted-foreground mt-1 leading-snug">{sub}</div>
              </div>
            ))}
          </div>

          <div className="mt-8">
            <div className="overline flex items-center gap-1.5 mb-2.5"><MapPin className="h-3.5 w-3.5" />Halls on the platform</div>
            <div className="flex flex-wrap gap-1.5">
              {HALLS.map(h => (
                <span key={h} className="text-[11px] text-foreground/70 bg-black/5 dark:bg-white/5 rounded-full px-2.5 py-1">{h}</span>
              ))}
            </div>
          </div>
        </div>

        {/* Right — form card */}
        <div className="w-full max-w-md mx-auto lg:mx-0">
          {/* Mobile brand */}
          <div className="lg:hidden mb-6 text-center">
            <h1 className="font-display text-4xl font-black tracking-tighter leading-[0.95]">
              Every hall,<br />
              <span className="bg-clip-text text-transparent bg-[linear-gradient(120deg,#0047FF,#22D3EE_60%,#BEF264)]">one sign-in.</span>
            </h1>
          </div>

          <div className="glass-strong rounded-3xl p-7 sm:p-8">
            <AnimatePresence mode="wait">
              {step === "email" ? (
                <motion.div key="email"
                  initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 16 }}
                  transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}>
                  <div className="mb-6">
                    <h2 className="font-display text-3xl font-extrabold tracking-tighter mb-1">Sign in</h2>
                    <p className="text-muted-foreground text-sm">Use your official <strong>@bmsit.in</strong> email to continue.</p>
                  </div>
                  <form onSubmit={sendOtp} className="space-y-4">
                    <div className="space-y-1.5">
                      <label className="text-mono text-[11px] uppercase tracking-widest text-muted-foreground">Email address</label>
                      <div className="relative">
                        <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                        <Input type="email" value={email} onChange={e => setEmail(e.target.value)}
                          placeholder="yourname@bmsit.in" className="pl-10" autoFocus required />
                      </div>
                    </div>
                    <Button type="submit" className="w-full rounded-xl" size="lg" disabled={busy}>
                      {busy ? "Sending code…" : "Send login code"} <ArrowRight className="h-4 w-4" />
                    </Button>
                  </form>
                  <p className="text-center text-xs text-muted-foreground mt-5">
                    A one-time code will be emailed to you. No password required.
                  </p>
                </motion.div>
              ) : (
                <motion.div key="otp"
                  initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }}
                  transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}>
                  <button type="button" onClick={() => { setStep("email"); setCode(""); }}
                    className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-5">
                    <ChevronLeft className="h-4 w-4" /> Back
                  </button>
                  <div className="mb-6">
                    <h2 className="font-display text-3xl font-extrabold tracking-tighter mb-1">Check your inbox</h2>
                    <p className="text-muted-foreground text-sm">
                      We sent a 6-digit code to <span className="font-semibold text-foreground">{email}</span>
                    </p>
                  </div>
                  <form onSubmit={verifyCode} className="space-y-4">
                    <div className="space-y-1.5">
                      <label className="text-mono text-[11px] uppercase tracking-widest text-muted-foreground">Verification code</label>
                      <div className="relative">
                        <KeyRound className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                        <Input type="text" inputMode="numeric" pattern="[0-9]*" maxLength={6} value={code}
                          onChange={e => setCode(e.target.value.replace(/\D/g, ""))}
                          placeholder="000000"
                          className="pl-10 text-center tracking-[0.6em] font-mono text-lg font-bold"
                          autoFocus autoComplete="one-time-code" />
                      </div>
                    </div>
                    <Button type="submit" className="w-full rounded-xl" size="lg" disabled={busy || code.length < 6}>
                      {busy ? "Verifying…" : "Sign in"} <ArrowRight className="h-4 w-4" />
                    </Button>
                    <button type="button" onClick={sendOtp} disabled={busy}
                      className="w-full text-sm text-center text-muted-foreground hover:text-cobalt-500 transition-colors">
                      Didn't receive it? <span className="font-medium underline underline-offset-2">Resend code</span>
                    </button>
                  </form>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Powered by Arventiq */}
          <div className="mt-6 flex items-center justify-center gap-3">
            <span className="text-[9px] text-mono uppercase tracking-widest text-muted-foreground">Secured by FortifyHub · Powered by</span>
            <a href="https://arventiqlabs.com" target="_blank" rel="noreferrer" className="opacity-80 hover:opacity-100 transition-opacity">
              <img src="/brand/arventiq-full.png" alt="Arventiq Labs" className="h-5 w-auto dark:brightness-0 dark:invert" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
